import { FC, ReactNode } from 'react';
import clsx from 'clsx';
import NumberUtil from 'utils/number';
import AnalyticsExportPageFrame, {
  type AnalyticsExportPageProfile,
} from './AnalyticsExportPageFrame';
import {
  getAnalyticsExportTileStyle,
  resolveAnalyticsExportColumns,
} from './constants/analyticsExportPageLayout';
import { analyticsExportTheme as theme } from './analyticsExportTheme';

export interface AnalyticsExportMetricTile {
  key: string;
  /** Reaction tiles identify themselves by icon, so the label is dropped. */
  label?: string;
  icon?: ReactNode;
  value: string;
  changePercent?: number;
}

const AnalyticsExportChangeBadge: FC<{ percentage: number }> = ({
  percentage,
}) => {
  const rounded = Number(percentage.toFixed(2)) || 0;
  const sign = Math.sign(rounded);
  const prefix = sign === 1 ? '+ ' : sign === -1 ? '- ' : '';

  return (
    <span
      className={clsx(
        'tw-inline-flex tw-items-center tw-rounded-full tw-px-3 tw-py-1 tw-text-base tw-font-semibold',
        {
          'tw-bg-success-20 tw-text-grayscale-190': sign === 1,
          'tw-bg-danger-20 tw-text-grayscale-190': sign === -1,
          [theme.neutralBadge]: sign === 0,
        },
      )}
    >
      {`${prefix}${NumberUtil.compact(Math.abs(rounded))}%`}
    </span>
  );
};

interface Props {
  title: string;
  subtitle: string;
  tiles: AnalyticsExportMetricTile[];
  profile: AnalyticsExportPageProfile;
  pageNumber: number;
  totalPages: number;
}

const AnalyticsExportSummaryPage: FC<Props> = ({
  title,
  subtitle,
  tiles,
  profile,
  pageNumber,
  totalPages,
}) => {
  const tileStyle = getAnalyticsExportTileStyle(
    resolveAnalyticsExportColumns(tiles.length),
  );

  return (
    <AnalyticsExportPageFrame
      title={title}
      subtitle={subtitle}
      profile={profile}
      pageNumber={pageNumber}
      totalPages={totalPages}
      dataCy="analytics_export-public_summary-page"
    >
      <div className="analytics-export-metric-grid tw-flex tw-flex-1 tw-flex-wrap tw-content-start tw-gap-4 tw-py-8">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            style={tileStyle}
            className={`analytics-export-metric-tile ${theme.tile} tw-flex tw-flex-col tw-justify-between tw-gap-4 tw-rounded-2xl tw-px-6 tw-py-4`}
          >
            {tile.icon ? (
              <span className="tw-flex tw-h-8 tw-w-8 tw-items-center">
                {tile.icon}
              </span>
            ) : (
              <span className={`tw-text-lg ${theme.tileLabel}`}>
                {tile.label}
              </span>
            )}
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
              <span
                className={`tw-text-3xl tw-font-bold tw-leading-none ${theme.tileValue}`}
              >
                {tile.value}
              </span>
              {tile.changePercent !== undefined ? (
                <AnalyticsExportChangeBadge percentage={tile.changePercent} />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </AnalyticsExportPageFrame>
  );
};

export default AnalyticsExportSummaryPage;
